import { Box, styled, Typography } from "@material-ui/core";
import { Check } from "@material-ui/icons";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import type { EpocusBillingReportQuery } from "./__generated__/billing-operations.gql";
import { BILLING_REPORT_PRINT_CLASS } from "./report-print";

type Report = EpocusBillingReportQuery["epocusBillingReport"];

const ReportRoot = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  overflow: "hidden",
});

const ReportHead = styled(Box)({
  backgroundColor: EPOCUS_PRIMARY,
  color: EPOCUS_WHITE,
  padding: "16px 18px",
});

const ReportBody = styled(Box)({
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 18,
});

const Grid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
});

const FieldLabel = styled(Typography)({
  fontSize: rem(10.5),
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const FieldValue = styled(Typography)({
  fontSize: rem(13.5),
  color: EPOCUS_TEXT_PRIMARY,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  marginBottom: 6,
});

const Prose = styled(Typography)({
  fontSize: rem(13.5),
  color: EPOCUS_TEXT_PRIMARY,
  lineHeight: 1.6,
  whiteSpace: "pre-line",
});

const CptBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  backgroundColor: EPOCUS_SELECTED_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 10,
  padding: "10px 12px",
});

const Thumbs = styled(Box)({ display: "flex", gap: 8, flexWrap: "wrap" });

const Thumb = styled("img")({
  width: 96,
  height: 96,
  objectFit: "cover",
  borderRadius: 8,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const ThumbFallback = styled(Box)({
  width: 96,
  height: 96,
  borderRadius: 8,
  border: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_SELECTED_BG,
});

const SignRow = styled(Box)({
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  paddingTop: 12,
});

const Field: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </Box>
);

const formatDate = (value: string | null | undefined): string =>
  value
    ? new Date(value).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export const BillingReportCard: FC<{
  report: Report;
  programName: string;
  accessionNumber: string | null;
}> = ({ report, programName, accessionNumber }) => {
  const provider = [report.renderingProvider.name, report.renderingProvider.npi]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

  return (
    <ReportRoot className={BILLING_REPORT_PRINT_CLASS}>
      <ReportHead>
        <Typography style={{ fontSize: rem(17), fontWeight: 700 }}>
          POCUS Study Report
        </Typography>
        <Typography style={{ fontSize: rem(12.5), opacity: 0.85 }}>
          {programName}
          {accessionNumber ? ` · Study ${accessionNumber}` : ""}
        </Typography>
      </ReportHead>

      <ReportBody>
        <Grid>
          <Field label="Patient" value={report.patientName ?? "—"} />
          <Field
            label="Identifiers (two)"
            value={report.identifiers.join(" · ") || "—"}
          />
          <Field
            label="Date of service"
            value={formatDate(report.dateOfService)}
          />
          <Field label="Exam" value={report.examLabel} />
          <Field label="Rendering provider" value={provider || "—"} />
          <Field label="Device" value={report.device ?? "—"} />
        </Grid>

        <Box>
          <SectionTitle>Clinical indication / medical necessity</SectionTitle>
          <Prose>{report.clinicalIndication ?? "Not recorded"}</Prose>
        </Box>

        <Box>
          <SectionTitle>Findings &amp; interpretation</SectionTitle>
          <Prose>
            {[report.findings, report.interpretation]
              .filter((part): part is string => Boolean(part))
              .join("\n\n") || "Not recorded"}
          </Prose>
        </Box>

        <Box>
          <SectionTitle>Archived image</SectionTitle>
          <Thumbs>
            {report.images.map((image) =>
              image.thumbnailUrl ? (
                <Thumb
                  key={image.imagingInstanceId}
                  src={image.thumbnailUrl}
                  alt="Archived study image"
                />
              ) : (
                <ThumbFallback key={image.imagingInstanceId} />
              ),
            )}
          </Thumbs>
          <Typography
            style={{
              fontSize: rem(12.5),
              color: EPOCUS_TEXT_SECONDARY,
              marginTop: 6,
            }}
          >
            {report.images.length}{" "}
            {report.images.length === 1 ? "image" : "images"} on file ·
            Retrievable from the ePOCUS archive · proof of study
          </Typography>
        </Box>

        <Box>
          <SectionTitle>Billing code</SectionTitle>
          {report.procedures.map((procedure) => (
            <CptBox key={`${procedure.cpt}${procedure.modifier ?? ""}`}>
              <Typography
                style={{
                  fontSize: rem(15),
                  fontWeight: 700,
                  color: EPOCUS_TEXT_PRIMARY,
                }}
              >
                {procedure.cpt}
                {procedure.modifier ?? ""}
              </Typography>
              <Typography
                style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
              >
                {procedure.description ?? "—"}
              </Typography>
              {procedure.clinicianConfirmed && (
                <Typography
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: rem(12),
                    fontWeight: 700,
                    color: EPOCUS_STATUS_REVIEWED_TEXT,
                    backgroundColor: EPOCUS_STATUS_REVIEWED_BG,
                    padding: "3px 8px",
                    borderRadius: 999,
                  }}
                >
                  <Check style={{ fontSize: rem(14) }} />
                  Clinician-confirmed
                </Typography>
              )}
            </CptBox>
          ))}
        </Box>

        <SignRow>
          <Typography
            style={{
              fontSize: rem(16),
              fontWeight: 600,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {report.signature.signedByName ?? "—"}
          </Typography>
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            Electronically signed · {formatDate(report.signature.signedAt)}
            {report.signature.auditLogged ? " · audit-logged" : ""}
          </Typography>
        </SignRow>
      </ReportBody>
    </ReportRoot>
  );
};
